import React, { useState } from 'react';
// React and hooks
import { useHistory } from 'react-router-dom';
import {
  Button,
  Col,
  Collapse,
  Row,
  Space,
  Typography,
  Card,
  Form,
  Select,
  message,
  Menu,
  Dropdown,
  Divider,
  Input,
  Spin,
  Modal,
  Alert,
  Tooltip,
  Table,
  Tag,
  Progress,
} from 'antd';
import {
  LoadingOutlined,
  ClockCircleOutlined,
  RocketOutlined,
} from '@ant-design/icons';

// Shared components
import { useCampaignRequestorContext } from '../../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { validateCampaignOrder, validateCampaignForLaunch } from '../../CampaignRequestor/CampaignRequestorUtil';
import { useMutation } from 'react-apollo';
import UPDATE_CAMPAIGN_STATUS from '../../../../core/GraphQl/Mutations/UPDATE_CAMPAIGN_STATUS';
import useCampaignLaunchProgress from '../../../../core/hooks/useCampaignLaunchProgress';

// Local components
import { CampaignOrderQueueReviewCampaign } from './CampaignOrderQueueReviewCampaign';
import CampaignLaunchProgressModal from './CampaignLaunchProgressModal';

// Styles
import '../../CampaignRequestor/styles.css';
import './styles.css';
import { getCampaignOrderErrorMessage } from '../../../../core/utils/constants/campaignOrderErrorType';
import { useCampaignOrderQueueContext } from '../../../../core/components/campaigns/campaignordersqueue/useCampaignOrderQueueContext';
import moment from 'moment';
import { formatCampaignName } from '../../../../core/utils/campaignNaming';
import { formatCurrency2SigFig } from '../../../../core/utils/campaigns';

const { Panel } = Collapse;
const { Title, Text } = Typography;

export const CampaignOrderQueueCampaignReview = ({ form, fieldName, campaignIndex, onMediaStatusUpdate, refetchCampaignOrder }) => {
  const { updateCampaignOrder, currentCampaignOrder, loadingUpdateCampaignOrder } =
    useCampaignRequestorContext();
  const { launchCampaignOrderProgrammatic, loadingLaunchCampaingOrderProgrammatic } =
    useCampaignOrderQueueContext();
  const [updateCampaignStatus, { loading: loadingUpdateCampaignStatus }] = useMutation(UPDATE_CAMPAIGN_STATUS);

  const campaign = form.getFieldValue('campaigns')[campaignIndex];
  const currentCampaign = currentCampaignOrder.campaigns[campaignIndex];
  const campaignId = currentCampaign?.id;
  const campaignStatus = currentCampaign?.status;

  // Check if any OTHER campaign in this campaign order is currently launching PROGRAMMATICALLY
  // Manual launches can happen in parallel, only programmatic launches need to be sequential
  const getOtherProgrammaticLaunchingCampaign = () => {
    return currentCampaignOrder.campaigns.find((camp, index) => {
      if (index === campaignIndex) return false; // Skip current campaign
      const isLaunchingProgrammatically =
        ['LAUNCHING_LIVE', 'LAUNCHING_PAUSED'].includes(camp.status) &&
        camp.isLaunchProgrammatically === true;
      return isLaunchingProgrammatically;
    });
  };

  const otherProgrammaticLaunchingCampaign = getOtherProgrammaticLaunchingCampaign();
  const isAnotherProgrammaticCampaignLaunching = !!otherProgrammaticLaunchingCampaign;

  // Check if Ad Provider ID is pre-populated (which prevents programmatic launch)
  const hasAdProviderId = !!(campaign?.adProviderId && campaign.adProviderId.trim() !== '');

  // Track campaign launch progress using campaign ID
  const isLaunching = ['LAUNCHING_LIVE', 'LAUNCHING_PAUSED'].includes(campaignStatus);
  const progressData = useCampaignLaunchProgress(campaignId, {
    enabled: isLaunching,
    autoCleanup: false,
    onCompleted: async (progress) => {
      console.log('Campaign launch completed, refetching campaign order data...');
      if (refetchCampaignOrder) {
        await refetchCampaignOrder();
        console.log('✅ Campaign order data refetched after completion');
      }
    },
    onFailed: async (progress) => {
      console.log('Campaign launch failed, refetching campaign order data...');
      if (refetchCampaignOrder) {
        await refetchCampaignOrder();
        console.log('✅ Campaign order data refetched after failure');
      }
    }
  });

  const [demandSidePlatform, setDemandSidePlatform] = useState(
    campaign ? campaign.demandSidePlatform : null
  );
  const [isManualLaunch, setIsManualLaunch] = useState(false);
  const [showStartDateModal, setShowStartDateModal] = useState(false);
  const [pendingLaunchStatus, setPendingLaunchStatus] = useState(null);
  const [isAdjustingDate, setIsAdjustingDate] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [currentLaunchStatus, setCurrentLaunchStatus] = useState(null);

  const history = useHistory();

  const save = async (status, options = {}) => {
    const { updateCampaignStatus = false, isManualLaunch = false, skipValidation = false } = options;
    console.log('=== save() START ===');
    console.log('status:', status);
    console.log('options:', options);

    // Pre-flight campaign order validation (skip if already validated in caller)
    if (!skipValidation) {
      console.log('Running validation...');
      if (isManualLaunch) {
        // For manual launch, only validate the specific campaign
        if (!validateCampaignForLaunch(currentCampaignOrder, campaignIndex, message)) {
          console.log('❌ Validation failed in save()');
          return;
        }
      } else {
        // For other operations, validate entire campaign order
        if (!validateCampaignOrder(currentCampaignOrder, message)) {
          console.log('❌ Validation failed in save()');
          return;
        }
      }
    } else {
      console.log('⏭️ Skipping validation (already done)');
    }

    // Validate form fields
    let values;
    try {
      if (skipValidation && isManualLaunch) {
        // For manual launch with skipValidation, only validate the specific campaign's fields
        console.log('Validating only campaign', campaignIndex, 'fields...');
        const fieldsToValidate = [
          ['campaigns', campaignIndex, 'demandSidePlatform'],
          ['campaigns', campaignIndex, 'adProviderId'],
          ['campaigns', campaignIndex, 'maxBid'],
        ];
        await form.validateFields(fieldsToValidate);
        console.log('✅ Campaign', campaignIndex, 'form validation passed');

        // Get all form values (we need the whole campaigns array for the mutation)
        values = form.getFieldsValue();
        console.log('Form values:', values);
      } else {
        // For other operations, validate all fields
        console.log('⚠️ Validating ALL form fields...');
        values = await form.validateFields();
        console.log('✅ All form validation passed');
      }
    } catch (error) {
      console.log('❌ Form validation FAILED in save():', error);
      message.error(error.errorFields[0].errors[0]);
      return;
    }

    try {

      console.log('🚀 Calling updateCampaignOrder mutation...');

      // For manual launch, only include the campaign being launched in the mutation
      const campaignsToUpdate = (skipValidation && isManualLaunch)
        ? [values.campaigns[campaignIndex]]  // Only the clicked campaign
        : values.campaigns;  // All campaigns for other operations

      console.log('Campaigns to update:', campaignsToUpdate);

      await updateCampaignOrder({
        variables: {
          where: { id: currentCampaignOrder.id },
          data: {
            status,
            campaigns: {
              update: campaignsToUpdate.map((campaign, index) => ({
                where: { id: campaign.id },
                data: {
                  demandSidePlatform: campaign.demandSidePlatform,
                  adProviderId: campaign.adProviderId || null,
                  maxBid: Number(campaign.maxBid || 0),

                  lineItems: {
                    update: campaign.lineItems.map((lineItem) => ({
                    where: { id: lineItem.id },
                    data: {
                      supplySidePlatform: lineItem.supplySidePlatform,
                      maxBid: Number(lineItem.maxBid || 0),
                      frequencyCap: Number(lineItem.frequencyCap || 0),
                      beacons: {
                        create: (lineItem.beacons || [])
                          .filter((beacon, index, array) =>
                            array.findIndex(b => b.beaconId === beacon.beaconId) === index
                          )
                          .map((beacon) => ({
                            beaconId: beacon.beaconId,
                            name: beacon.name,
                            advertiserId: beacon.advertiserId,
                            beaconType: beacon.beaconType,
                          })),
                      },
                      dealIds: {
                        create: lineItem.dealIds.map((data) => ({
                          dealId: data.dealId,
                          name: data.name,
                          org: {
                            connect: {
                              id: currentCampaignOrder.advertiser.id,
                            },
                          },
                        })),
                      },
                    },
                  })),
                },
                },
              })),
            },
          },
        },
      });
      console.log('✅ updateCampaignOrder mutation COMPLETED successfully');
    } catch (error) {
      console.log('❌ updateCampaignOrder mutation FAILED:', error);
      message.destroy();
      message.error(getCampaignOrderErrorMessage(error));
    }
    console.log('=== save() END ===');
  };

  const handleLaunchManual = async () => {
    console.log('=== handleLaunchManual START ===');
    console.log('campaignIndex:', campaignIndex);
    console.log('currentCampaignOrder:', currentCampaignOrder);

    setIsManualLaunch(true);

    // Pre-flight validation for this specific campaign only
    console.log('Starting validation for campaign at index:', campaignIndex);
    const validationResult = validateCampaignForLaunch(currentCampaignOrder, campaignIndex, message);
    console.log('Validation result:', validationResult);

    if (!validationResult) {
      console.log('❌ Validation FAILED - aborting launch');
      setIsManualLaunch(false);
      return;
    }
    console.log('✅ Validation PASSED - proceeding with launch');

    try {
      console.log('Validating form fields for campaign at index:', campaignIndex);
      // Only validate fields for this specific campaign
      const fieldsToValidate = [
        ['campaigns', campaignIndex, 'demandSidePlatform'],
        ['campaigns', campaignIndex, 'adProviderId'],
        ['campaigns', campaignIndex, 'maxBid'],
      ];
      console.log('Fields to validate:', fieldsToValidate);
      await form.validateFields(fieldsToValidate);
      console.log('✅ Form validation PASSED');
    } catch (error) {
      console.log('❌ Form validation FAILED:', error);
      setIsManualLaunch(false);
      message.warning('Please fix the validation errors before launching.');
      return;
    }

    try {
      message.loading('Launching campaign...', 0);

      // Get form values for the clicked campaign
      const values = form.getFieldsValue();
      const campaignData = values.campaigns[campaignIndex];
      const campaignId = currentCampaignOrder.campaigns[campaignIndex].id;

      console.log('Step 1: Updating campaign status to LIVE...');
      console.log('Campaign ID:', campaignId);
      console.log('Campaign data:', campaignData);

      // Step 1: Update the campaign status (manual launches are always LIVE)
      await updateCampaignStatus({
        variables: {
          where: { id: campaignId },
          data: {
            status: 'LIVE',
            demandSidePlatform: campaignData.demandSidePlatform,
            adProviderId: campaignData.adProviderId || null,
            maxBid: Number(campaignData.maxBid || 0),
            isLaunchProgrammatically: false,  // Manual launch
            lineItems: {
              update: campaignData.lineItems.map((lineItem) => ({
                where: { id: lineItem.id },
                data: {
                  supplySidePlatform: lineItem.supplySidePlatform,
                  maxBid: Number(lineItem.maxBid || 0),
                  frequencyCap: Number(lineItem.frequencyCap || 0),
                  beacons: {
                    create: (lineItem.beacons || [])
                      .filter((beacon, index, array) =>
                        array.findIndex(b => b.beaconId === beacon.beaconId) === index
                      )
                      .map((beacon) => ({
                        beaconId: beacon.beaconId,
                        name: beacon.name,
                        advertiserId: beacon.advertiserId,
                        beaconType: beacon.beaconType,
                      })),
                  },
                  dealIds: {
                    create: lineItem.dealIds.map((data) => ({
                      dealId: data.dealId,
                      name: data.name,
                      org: {
                        connect: {
                          id: currentCampaignOrder.advertiser.id,
                        },
                      },
                    })),
                  },
                },
              }))
            }
          },
        },
      });
      console.log('✅ Campaign status updated successfully');

      console.log('Step 2: Updating campaign order status to LIVE...');

      // Step 2: Update the campaign order status
      await updateCampaignOrder({
        variables: {
          where: { id: currentCampaignOrder.id },
          data: {
            status: 'LIVE',
          },
        },
      });
      console.log('✅ Campaign order status updated successfully');

      message.destroy();
      message.success('Campaign launched successfully');

      // Step 3: Refresh campaign order data to show updated status
      if (refetchCampaignOrder) {
        console.log('Step 3: Refetching campaign order...');
        await refetchCampaignOrder();
        console.log('✅ Refetch completed');
      }

      console.log('=== handleLaunchManual COMPLETE ===');
    } catch (error) {
      console.log('❌ Error during launch:', error);
      message.destroy();
      message.error(getCampaignOrderErrorMessage(error));
    } finally {
      setIsManualLaunch(false);
    }
  };

  const validateProgrammaticLaunch = async (options = {}) => {
    const { skipDateValidation = false } = options;

    console.log('=== validateProgrammaticLaunch START ===');
    console.log('campaignIndex:', campaignIndex);
    console.log('skipDateValidation:', skipDateValidation);

    console.log('Starting validation for campaign at index:', campaignIndex);
    const validationResult = validateCampaignForLaunch(currentCampaignOrder, campaignIndex, message);
    console.log('Validation result:', validationResult);

    if (!validationResult) {
      console.log('❌ Validation FAILED - aborting');
      return {
        isValid: false,
        shouldShowDateModal: false,
        validationError: 'Pre-flight validation failed'
      };
    }
    console.log('✅ Pre-flight validation PASSED');

    try {
      console.log('Validating form fields for campaign at index:', campaignIndex);

      const campaignFieldsToValidate = [
        ['campaigns', campaignIndex, 'demandSidePlatform'],
        ['campaigns', campaignIndex, 'adProviderId'],
        ['campaigns', campaignIndex, 'maxBid'],
      ];

      const campaign = form.getFieldValue('campaigns')[campaignIndex];
      const lineItemFieldsToValidate = [];
      if (campaign && campaign.lineItems) {
        campaign.lineItems.forEach((lineItem, lineItemIndex) => {
          lineItemFieldsToValidate.push(
            ['campaigns', campaignIndex, 'lineItems', lineItemIndex, 'supplySidePlatform'],
            ['campaigns', campaignIndex, 'lineItems', lineItemIndex, 'maxBid'],
            ['campaigns', campaignIndex, 'lineItems', lineItemIndex, 'frequencyCap']
          );
        });
      }

      const allFieldsToValidate = [...campaignFieldsToValidate, ...lineItemFieldsToValidate];
      console.log('Fields to validate:', allFieldsToValidate);

      await form.validateFields(allFieldsToValidate);
      console.log('✅ Form validation PASSED');
    } catch (error) {
      console.log('❌ Form validation FAILED:', error);
      message.warning('Please fix the validation errors before launching.');
      return {
        isValid: false,
        shouldShowDateModal: false,
        validationError: 'Form validation failed'
      };
    }

    if (!skipDateValidation) {
      const currentCampaign = currentCampaignOrder.campaigns[campaignIndex];
      const { startDate } = currentCampaign;
      const today = moment().startOf('day');
      const campaignStartDate = moment(startDate).startOf('day');

      if (campaignStartDate.isBefore(today)) {
        console.log('⚠️ Campaign start date is in the past, date modal required');
        return {
          isValid: true,
          shouldShowDateModal: true,
          validationError: null
        };
      }
    }

    console.log('✅ All validation PASSED');
    console.log('=== validateProgrammaticLaunch END ===');

    return {
      isValid: true,
      shouldShowDateModal: false,
      validationError: null
    };
  };

  const handleUpdateDatesOnly = async () => {
    console.log('=== handleUpdateDatesOnly START ===');

    try {
      const today = moment().startOf('day').toISOString();
      const currentCampaign = currentCampaignOrder.campaigns[campaignIndex];
      console.log('Updating campaign start date to:', today);
      console.log('Campaign ID:', currentCampaign.id);

      await updateCampaignStatus({
        variables: {
          where: { id: currentCampaign.id },
          data: {
            startDate: today
          }
        }
      });

      console.log('✅ Campaign start date updated successfully');
      message.success('Campaign start date adjusted to today');

      if (refetchCampaignOrder) {
        await refetchCampaignOrder();
        console.log('✅ Campaign order data refetched');
      }

      return { success: true };
    } catch (error) {
      console.error('❌ Error updating campaign start date:', error);
      message.error('Failed to update campaign start date');
      return { success: false, error };
    } finally {
      console.log('=== handleUpdateDatesOnly END ===');
    }
  };

  const handleLaunchProgrammatic = async (launchStatus = 'pause') => {
    console.log('=== handleLaunchProgrammatic START ===');
    console.log('campaignIndex:', campaignIndex);
    console.log('launchStatus:', launchStatus);

    setIsManualLaunch(false);

    // Check if Ad Provider ID is already provided - prevent programmatic launch
    const campaignData = form.getFieldValue('campaigns')[campaignIndex];
    if (campaignData?.adProviderId && campaignData.adProviderId.trim() !== '') {
      return;
    }

    const validation = await validateProgrammaticLaunch();

    if (!validation.isValid) {
      console.log('❌ Validation FAILED - aborting launch');
      return;
    }

    if (validation.shouldShowDateModal) {
      console.log('⚠️ Start date is in the past, showing adjustment modal');
      setPendingLaunchStatus(launchStatus);
      setShowStartDateModal(true);
      return;
    }

    console.log('✅ All validation PASSED - proceeding with launch');

    try {
      // Store the launch status for completion handling
      setCurrentLaunchStatus(launchStatus);

      // Get form values for the clicked campaign
      const values = form.getFieldsValue();
      const campaignData = values.campaigns[campaignIndex];
      const campaignId = currentCampaignOrder.campaigns[campaignIndex].id;

      console.log('Step 1: Updating campaign status to APPROVED for programmatic launch...');
      console.log('Campaign ID:', campaignId);
      console.log('Campaign data:', campaignData);

      // Step 1: Update the campaign status to APPROVED (will be updated to LIVE/PAUSED after external service completes)
      await updateCampaignStatus({
        variables: {
          where: { id: campaignId },
          data: {
            status: launchStatus === 'play' ? 'LAUNCHING_LIVE' : 'LAUNCHING_PAUSED',
            demandSidePlatform: campaignData.demandSidePlatform,
            adProviderId: null,  // Always null for programmatic launch - will be populated by DSP
            maxBid: Number(campaignData.maxBid || 0),
            isLaunchProgrammatically: true,  // Programmatic launch
            lineItems: {
              update: campaignData.lineItems.map((lineItem) => ({
                where: { id: lineItem.id },
                data: {
                  supplySidePlatform: lineItem.supplySidePlatform,
                  maxBid: Number(lineItem.maxBid || 0),
                  frequencyCap: Number(lineItem.frequencyCap || 0),
                  beacons: {
                    create: (lineItem.beacons || [])
                      .filter((beacon, index, array) =>
                        array.findIndex(b => b.beaconId === beacon.beaconId) === index
                      )
                      .map((beacon) => ({
                        beaconId: beacon.beaconId,
                        name: beacon.name,
                        advertiserId: beacon.advertiserId,
                        beaconType: beacon.beaconType,
                      })),
                  },
                  dealIds: {
                    create: lineItem.dealIds.map((data) => ({
                      dealId: data.dealId,
                      name: data.name,
                      org: {
                        connect: {
                          id: currentCampaignOrder.advertiser.id,
                        },
                      },
                    })),
                  },
                },
              }))
            }
          },
        },
      });
      console.log('✅ Campaign status updated to APPROVED successfully');

      console.log('Step 2: Calling save to update line items...');
      // Status update will be handled on the backend
      // await save(currentCampaignOrder.status);

      console.log('Step 3: Calling launchCampaignOrderProgrammatic mutation...');
      await launchCampaignOrderProgrammatic({
        variables: {
          data: {
            id: currentCampaignOrder.id,
            launchStatus, // pass "play" or "pause"
            alternativeName: formatCampaignName({
              ...currentCampaignOrder,
              name: currentCampaignOrder.campaigns[campaignIndex].name,
            }),
            campaignId,
          },
        },
      });

      console.log('✅ Campaign launch initiated successfully');

      message.success(`Campaign launch initiated with status: ${launchStatus === 'play' ? 'LIVE' : 'PAUSED'}`);

      // Refetch campaign order data to show updated status
      if (refetchCampaignOrder) {
        console.log('Refetching campaign order...');
        await refetchCampaignOrder();
        console.log('✅ Refetch completed');
      }
    } catch (error) {
      console.error('❌ Error during programmatic launch:', error);
      message.error(getCampaignOrderErrorMessage(error));
    }

    console.log('=== handleLaunchProgrammatic END ===');
  };

  const handleLaunchWithAdjustedDate = async () => {
    console.log('=== handleLaunchWithAdjustedDate START ===');
    console.log('campaignIndex:', campaignIndex);

    if (isAdjustingDate) return;

    setIsAdjustingDate(true);

    try {
      const validation = await validateProgrammaticLaunch({ skipDateValidation: true });

      if (!validation.isValid) {
        console.log('❌ Validation FAILED - aborting');
        return;
      }

      console.log('✅ Validation PASSED - proceeding with date update and launch');

      const result = await handleUpdateDatesOnly();

      if (!result.success) {
        console.log('❌ Date update FAILED - aborting launch');
        return;
      }

      setShowStartDateModal(false);

      const launchStatus = pendingLaunchStatus;
      setPendingLaunchStatus(null);

      await handleLaunchProgrammatic(launchStatus);

    } catch (error) {
      console.error('❌ Error during adjust date and launch:', error);
      message.error(getCampaignOrderErrorMessage(error));
    } finally {
      setIsAdjustingDate(false);
    }

    console.log('=== handleLaunchWithAdjustedDate END ===');
  };

  return (
    <Spin spinning={loadingLaunchCampaingOrderProgrammatic || loadingUpdateCampaignOrder || loadingUpdateCampaignStatus}>
      <div id={`campaign-${campaignIndex}`}>
        <Collapse bordered={false} defaultActiveKey={[0]}>
          <Panel open={true} header={<Title level={5}>Campaign</Title>}>
          <Card>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {/* Show alert when another programmatic campaign is launching */}
              {isAnotherProgrammaticCampaignLaunching &&
               !isLaunching && (
                <Alert
                  message="Sequential Launch in Progress"
                  description={
                    <span>
                      Campaign "<strong>{otherProgrammaticLaunchingCampaign?.name || 'Another campaign'}</strong>" is currently launching programmatically.
                      Please wait for it to complete before launching this campaign programmatically. Manual launches are still available.
                    </span>
                  }
                  type="info"
                  showIcon
                  icon={<LoadingOutlined />}
                  closable={false}
                />
              )}
              {/* Show DSP/Ad Provider ID/Max Bid fields when campaign order is approved (matches line item launch details logic) */}
              {!['PENDING', 'LIVE_PENDING'].includes(currentCampaignOrder.status) &&
               !['LIVE', 'PAUSED'].includes(currentCampaignOrder.campaigns[campaignIndex]?.status) && (
                <>
                  <Row gutter={16}>
                    <Col span={6}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Text>DSP</Text>
                        <Text type="secondary">
                          <small> Demand-Side Platform </small>
                        </Text>
                        <Form.Item
                          name={[fieldName, 'demandSidePlatform']}
                          rules={[
                            {
                              required: true,
                              message: 'Please select a DSP',
                            },
                          ]}
                        >
                          <Select
                            style={{ width: '100%' }}
                            showSearch
                            optionFilterProp="children"
                            onChange={(value) => setDemandSidePlatform(value)}
                            filterOption={(input, option) =>
                              option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                            options={[
                              { value: 'NEXXEN', label: 'NEXXEN' },
                              { value: 'LIQUIDM', label: 'LIQUIDM' },
                              { value: 'JAMLOOP', label: 'JAMLOOP' },
                            ]}
                            disabled={isLaunching}
                          />
                        </Form.Item>
                      </Space>
                    </Col>
                    <Col span={6}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Text>Ad Provider ID</Text>
                        <Text type="secondary">
                          <small> Unique identifier for the ad provider </small>
                        </Text>
                        <Form.Item
                          name={[fieldName, 'adProviderId']}
                          rules={[
                            {
                              validator: (_, value) => {
                                if (isManualLaunch && (!value || value.trim() === '')) {
                                  return Promise.reject(
                                    'Ad Provider ID is required for manual launch'
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Input
                            style={{ width: '100%' }}
                            onChange={(e) =>
                              form.setFieldsValue({
                                campaigns: form
                                  .getFieldValue('campaigns')
                                  .map((c, idx) =>
                                    idx === campaignIndex
                                      ? { ...c, adProviderId: e.target.value }
                                      : c
                                  ),
                              })
                            }
                            value={(campaign && campaign.adProviderId) || ''}
                            disabled={isLaunching}
                          />
                        </Form.Item>
                      </Space>
                    </Col>
                    <Col span={7}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Text>Maximum Bid</Text>
                        <Text type="secondary">
                          <small> Maximum bid amount for the campaign </small>
                        </Text>
                        <Form.Item
                          name={[fieldName, 'maxBid']}
                          rules={[
                            {
                              required: true,
                              message: 'Maximum bid is required',
                            },
                            {
                              type: 'number',
                              min: 0.01,
                              message: 'Maximum bid must be greater than zero',
                            },
                          ]}
                        >
                          <Input
                            type="number"
                            step="0.01"
                            style={{ width: '100%' }}
                            onChange={(e) => {
                              const value = e.target.value;
                              const numericValue = value === '' ? null : parseFloat(value);
                              form.setFieldsValue({
                                campaigns: form
                                  .getFieldValue('campaigns')
                                  .map((c, idx) =>
                                    idx === campaignIndex
                                      ? { ...c, maxBid: numericValue }
                                      : c
                                  ),
                              });
                            }}
                            value={(campaign && campaign.maxBid) || 4.00}
                            disabled={isLaunching}
                          />
                        </Form.Item>
                      </Space>
                    </Col>
                  </Row>
                </>
              )}

              {/* Campaign Details Section */}
              <Row gutter={20}>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Campaign Type</Text>
                    <Text>
                      {(() => {
                        const typeLabels = {
                          // Numeric types
                          1: 'Display',
                          7: 'External',
                          8: 'Video',
                          // String types
                          'DISPLAY': 'Display',
                          'EXTERNAL': 'External',
                          'VIDEO': 'Video',
                        };
                        const campaignType = currentCampaignOrder.campaigns[campaignIndex]?.type;
                        return typeLabels[campaignType] || 'Display';
                      })()}
                    </Text>
                  </Space>
                </Col>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Campaign Name</Text>
                    <Text>
                      {currentCampaignOrder.campaigns[campaignIndex]?.name || `Campaign ${campaignIndex + 1}`}
                    </Text>
                  </Space>
                </Col>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Budget</Text>
                    <Text>
                      {formatCurrency2SigFig(currentCampaignOrder.campaigns[campaignIndex]?.budgetTotal || 0)}
                    </Text>
                  </Space>
                </Col>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Date Range</Text>
                    <Text>
                      {currentCampaignOrder.campaigns[campaignIndex]?.startDate &&
                       currentCampaignOrder.campaigns[campaignIndex]?.endDate
                        ? `${moment(currentCampaignOrder.campaigns[campaignIndex].startDate).format('MM/DD/YYYY')} - ${moment(currentCampaignOrder.campaigns[campaignIndex].endDate).format('MM/DD/YYYY')}`
                        : 'Not specified'}
                    </Text>
                  </Space>
                </Col>
              </Row>
              <Row gutter={20} style={{ marginTop: 16 }}>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Status</Text>
                    {(() => {
                      const status = currentCampaignOrder.campaigns[campaignIndex]?.status;
                      const statusConfig = {
                        PENDING: { color: 'default', text: 'Pending' },
                        APPROVED: { color: 'blue', text: 'Approved' },
                        LAUNCHING: { color: 'processing', text: 'Launching' },
                        LIVE: { color: 'success', text: 'Live' },
                        PAUSED: { color: 'warning', text: 'Paused' },
                        COMPLETED: { color: 'default', text: 'Completed' },
                        ARCHIVED: { color: 'default', text: 'Archived' },
                        ERROR: { color: 'error', text: 'Error' },
                      };
                      const config = statusConfig[status] || { color: 'default', text: status || 'Not Set' };
                      return <Tag color={config.color}>{config.text}</Tag>;
                    })()}
                  </Space>
                </Col>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Launch Type</Text>
                    {(() => {
                      const status = currentCampaignOrder.campaigns[campaignIndex]?.status;
                      const isLaunchProgrammatically = currentCampaignOrder.campaigns[campaignIndex]?.isLaunchProgrammatically;

                      if (!['LIVE', 'PAUSED'].includes(status)) {
                        return <Text type="secondary">-</Text>;
                      }

                      if (isLaunchProgrammatically) {
                        return <Tag color="purple">Programmatic</Tag>;
                      }
                      return <Tag color="blue">Manual</Tag>;
                    })()}
                  </Space>
                </Col>
              </Row>

              {/* Campaign Launch Progress Section - Only show for programmatic launches */}
              {isLaunching && currentCampaignOrder.campaigns[campaignIndex]?.isLaunchProgrammatically && (
                <>
                  <Divider plain style={{ margin: '16px 0' }} />
                  <Alert
                    message={
                      <Space>
                        <RocketOutlined style={{ fontSize: 16 }} />
                        <Text strong>Campaign Launch in Progress</Text>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: 12 }}>
                        {/* Progress Bar */}
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                            <Text strong style={{ marginRight: 8 }}>Progress:</Text>
                            <Text type="secondary">{progressData.progress || 0}%</Text>
                          </div>
                          <Progress
                            percent={progressData.progress || 0}
                            size="default"
                            status={
                              progressData.isFailed
                                ? 'exception'
                                : progressData.isCompleted
                                ? 'success'
                                : 'active'
                            }
                            strokeWidth={10}
                          />
                        </div>

                        {/* Current Phase and Message */}
                        <div>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            {progressData.phase && (
                              <div>
                                <Text strong>Phase: </Text>
                                <Tag color="blue">{progressData.phase}</Tag>
                              </div>
                            )}
                            <div>
                              <Text strong>Status: </Text>
                              <Text type="secondary">
                                {progressData.message || 'Initializing launch...'}
                              </Text>
                            </div>
                          </Space>
                        </div>

                        {/* Elapsed Time */}
                        {progressData.elapsedTimeFormatted && (
                          <div>
                            <Space>
                              <ClockCircleOutlined style={{ color: '#1890ff' }} />
                              <Text strong>Elapsed Time: </Text>
                              <Text style={{ color: '#1890ff' }}>
                                {progressData.elapsedTimeFormatted}
                              </Text>
                            </Space>
                          </div>
                        )}

                        {/* Error Details */}
                        {progressData.isFailed && progressData.progressData?.metadata?.errorDetails && (
                          <Alert
                            message="Launch Failed"
                            description={progressData.progressData.metadata.errorDetails}
                            type="error"
                            showIcon
                            style={{ marginTop: 8 }}
                          />
                        )}
                      </Space>
                    }
                    type={
                      progressData.isFailed
                        ? 'error'
                        : progressData.isCompleted
                        ? 'success'
                        : 'info'
                    }
                    showIcon
                    icon={
                      progressData.isFailed ? null : progressData.isCompleted ? null : <LoadingOutlined />
                    }
                  />
                </>
              )}

              <Divider plain></Divider>

              <Form.List name={[fieldName, 'lineItems']}>
                {(fields) => (
                  <>
                    {fields.map(
                      ({ key, name: fieldNameLineItems, fieldKey, ...restField }, index) => (
                        <>
                          <CampaignOrderQueueReviewCampaign
                            key={key}
                            form={form}
                            restField={restField}
                            fieldName={fieldNameLineItems}
                            fieldKey={fieldKey}
                            lineItemIndex={index}
                            campaignIndex={campaignIndex}
                            onMediaStatusUpdate={onMediaStatusUpdate}
                            isLaunching={isLaunching}
                          />
                          <Divider />
                        </>
                      )
                    )}
                  </>
                )}
              </Form.List>

              <>
                {!['LIVE', 'PAUSED'].includes(currentCampaignOrder.campaigns[campaignIndex]?.status) &&
                 !['PENDING', 'LIVE_PENDING'].includes(currentCampaignOrder.status) &&
                 !isLaunching && (
                  <>
                    {demandSidePlatform === 'NEXXEN' ? (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'flex-end',
                          gap: 12,
                          width: '100%',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <Button
                          style={{ width: 140 }}
                          type="default"
                          disabled={loadingUpdateCampaignOrder}
                          onClick={() => handleLaunchManual()}
                        >
                          Launch Manual
                        </Button>
                        <Tooltip
                          title={
                            hasAdProviderId
                              ? 'Cannot launch programmatically when Ad Provider ID is already provided. The Ad Provider ID should be populated by the DSP during programmatic launch.'
                              : isAnotherProgrammaticCampaignLaunching
                              ? `Please wait: "${otherProgrammaticLaunchingCampaign?.name || 'Another campaign'}" is currently launching`
                              : ''
                          }
                        >
                          <span style={{ display: 'inline-block' }}>
                            <Dropdown
                              trigger={['click']}
                              disabled={hasAdProviderId || isAnotherProgrammaticCampaignLaunching || loadingUpdateCampaignOrder || loadingLaunchCampaingOrderProgrammatic}
                              overlay={
                                <Menu>
                                  <Menu.Item onClick={() => handleLaunchProgrammatic('play')}>
                                    <span>Launch Auto Play</span>
                                  </Menu.Item>
                                  <Menu.Item onClick={() => handleLaunchProgrammatic('pause')}>
                                    <span>Launch Pause</span>
                                  </Menu.Item>
                                </Menu>
                              }
                            >
                              <Button
                                style={{ width: 200 }}
                                type="primary"
                                disabled={
                                  hasAdProviderId || loadingUpdateCampaignOrder || loadingLaunchCampaingOrderProgrammatic || isAnotherProgrammaticCampaignLaunching
                                }
                              >
                                Launch Programmatically
                              </Button>
                            </Dropdown>
                          </span>
                        </Tooltip>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                        }}
                      >
                        <Button
                          style={{ width: 100 }}
                          type="primary"
                          disabled={loadingUpdateCampaignOrder}
                          onClick={() => handleLaunchManual()}
                        >
                          Launch
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </>
            </Space>
          </Card>
          </Panel>
        </Collapse>
      </div>

      <Modal
        title="Start Date Adjustment"
        open={showStartDateModal}
        onOk={handleLaunchWithAdjustedDate}
        onCancel={() => {
          setShowStartDateModal(false);
          setPendingLaunchStatus(null);
        }}
        okText="Yes, adjust and launch"
        cancelText="Cancel"
        confirmLoading={isAdjustingDate}
      >
        <p>
          The campaign start date is behind today's date. We will adjust it to today's date and proceed with the launch.
        </p>
        <p>Do you want to continue?</p>
      </Modal>

      <CampaignLaunchProgressModal
        visible={showProgressModal}
        campaignOrderId={currentCampaignOrder?.id}
        campaignName={currentCampaignOrder?.name || 'Campaign'}
        onCancel={() => setShowProgressModal(false)}
        onCompleted={() => {
          setShowProgressModal(false);
          message.success('Campaign launched successfully!');
          // Navigate based on the launch status
          if (currentLaunchStatus === 'pause') {
            history.push('/campaigns/v2/orderqueue/paused');
          } else {
            history.push('/campaigns/v2/orderqueue/launched');
          }
        }}
        onFailed={(progressData) => {
          setShowProgressModal(false);
          const errorMsg = progressData?.metadata?.errorDetails || 'Campaign launch failed';
          message.error(errorMsg);
          // Failed campaigns might remain in launching tab with error status
        }}
        onError={(error) => {
          console.error('Progress subscription error:', error);
          // Keep modal open but show connection error within modal
        }}
      />
    </Spin>
  );
};
